import { useState } from 'react';
import {
  Box,
  HStack,
  Spinner,
  Text,
  VStack,
} from '@chakra-ui/react';
import { PlayerIdentity } from '../../../components/PlayerIdentity';
import { RiftboundButton } from '../../../components/ui/button';
import { RiftboundPlayerAvatar } from '../../../components/RiftboundPlayerAvatar';
import { riftboundToaster } from '../../../components/ui/toaster';
import { useAuth } from '../../../hooks/useAuth';
import {
  usePendingInvites,
  useSentInvites,
  useTeamInvites,
  type PendingInviteWithTeam,
} from '../hooks/useTeamInvites';
import { JoinTeamConsentSheet } from './JoinTeamConsentSheet';
import { PlayerDirectoryTypeahead } from './PlayerDirectoryTypeahead';

export interface TeamInviteListProps {
  teamId: string;
  /** UIDs already on the team — excluded from invite search. */
  memberUids?: string[];
  canManage?: boolean;
}

export function TeamInviteList({
  teamId,
  memberUids = [],
  canManage = true,
}: TeamInviteListProps) {
  const { sendInvite, revokeInvite, loading } = useTeamInvites();
  const { invites, loading: listLoading } = useSentInvites(teamId);
  const [sending, setSending] = useState(false);

  const handleSelect = async (player: {
    uid: string;
    displayName: string;
    slug: string;
    avatarUrl: string | null;
  }) => {
    setSending(true);
    try {
      await sendInvite(teamId, player.uid, {
        displayName: player.displayName,
        slug: player.slug,
        avatarUrl: player.avatarUrl,
      });
    } catch (err) {
      riftboundToaster.create({
        title: 'Invite failed',
        description: err instanceof Error ? err.message : 'Could not send invite.',
        type: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    try {
      await revokeInvite(teamId, inviteId);
    } catch (err) {
      riftboundToaster.create({
        title: 'Could not cancel invite',
        description: err instanceof Error ? err.message : 'Something went wrong.',
        type: 'error',
      });
    }
  };

  return (
    <VStack align="stretch" gap={5}>
      {canManage ? (
        <Box>
          <Text fontWeight="semibold" fontSize="sm" mb={1}>
            Invite players
          </Text>
          <Text fontSize="xs" color="textMuted" mb={2}>
            Search by handle and send an invite.
          </Text>
          <PlayerDirectoryTypeahead
            onSelect={(p) => void handleSelect(p)}
            excludeUids={memberUids}
            disabled={sending || loading}
          />
        </Box>
      ) : null}

      <Box>
        <Text fontWeight="semibold" fontSize="sm" mb={1}>
          Pending invites
        </Text>
        <Text fontSize="xs" color="textMuted" mb={2}>
          Outgoing invites waiting for a response. You can cancel them anytime.
        </Text>
        {listLoading ? (
          <HStack color="textMuted" py={2}>
            <Spinner size="xs" />
            <Text fontSize="sm">Loading invites…</Text>
          </HStack>
        ) : invites.length === 0 ? (
          <Text fontSize="sm" color="textMuted">
            No pending invites.
          </Text>
        ) : (
          <VStack align="stretch" gap={2}>
            {invites.map((invite) => (
              <HStack
                key={invite.id}
                justify="space-between"
                gap={3}
                p={3}
                borderWidth="hairline"
                borderColor="border"
                borderRadius="md"
                bg="bgElevated"
              >
                <PlayerIdentity
                  name={invite.inviteeName}
                  slug={invite.inviteeSlug}
                  photoURL={invite.inviteeAvatarUrl}
                />
                {canManage && invite.id ? (
                  <RiftboundButton
                    size="xs"
                    colorPalette="red"
                    onClick={() => void handleRevoke(invite.id!)}
                    disabled={loading}
                  >
                    Cancel
                  </RiftboundButton>
                ) : null}
              </HStack>
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
}

/** Pending invites for the signed-in user (accept / decline with share consent). */
export function PendingInvitesSection() {
  const { user } = useAuth();
  const { invites, loading } = usePendingInvites();
  const { acceptInvite, declineInvite, loading: actionLoading } = useTeamInvites();
  const [consentInvite, setConsentInvite] = useState<PendingInviteWithTeam | null>(null);
  const [shareCollection, setShareCollection] = useState(true);
  const [busy, setBusy] = useState(false);

  const defaultShare =
    user?.collectionVisibility === 'public' || user?.collectionVisibility === 'team';

  const openConsent = (invite: PendingInviteWithTeam) => {
    setShareCollection(defaultShare !== false);
    setConsentInvite(invite);
  };

  const handleAccept = async () => {
    if (!consentInvite?.id) return;
    setBusy(true);
    try {
      await acceptInvite(consentInvite.teamId, consentInvite.id, shareCollection);
      setConsentInvite(null);
    } catch (err) {
      riftboundToaster.create({
        title: 'Could not accept invite',
        description: err instanceof Error ? err.message : 'Something went wrong.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleJoinWithoutSharing = async () => {
    if (!consentInvite?.id) return;
    setBusy(true);
    try {
      await acceptInvite(consentInvite.teamId, consentInvite.id, false);
      setConsentInvite(null);
    } catch (err) {
      riftboundToaster.create({
        title: 'Could not accept invite',
        description: err instanceof Error ? err.message : 'Something went wrong.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const closeConsent = () => {
    if (!busy) setConsentInvite(null);
  };

  const handleDecline = async (invite: PendingInviteWithTeam) => {
    if (!invite.id) return;
    try {
      await declineInvite(invite.teamId, invite.id);
    } catch (err) {
      riftboundToaster.create({
        title: 'Could not decline',
        description: err instanceof Error ? err.message : 'Something went wrong.',
        type: 'error',
      });
    }
  };

  if (!user) return null;

  if (loading) {
    return (
      <HStack color="textMuted" py={2}>
        <Spinner size="xs" />
        <Text fontSize="sm">Loading invites…</Text>
      </HStack>
    );
  }

  if (invites.length === 0) {
    return (
      <Text fontSize="sm" color="textMuted">
        No pending team invites.
      </Text>
    );
  }

  return (
    <>
      <VStack align="stretch" gap={2}>
        {invites.map((invite) => (
          <HStack
            key={`${invite.teamId}-${invite.id}`}
            gap={3}
            p={3}
            borderWidth="hairline"
            borderColor="border"
            borderRadius="md"
            bg="bgElevated"
            align="center"
          >
            <RiftboundPlayerAvatar
              name={invite.teamName}
              photoURL={invite.teamAvatarUrl}
              size="sm"
              flexShrink={0}
            />
            <Box flex={1} minW={0}>
              <Text fontSize="sm" fontWeight="semibold" lineClamp={1}>
                {invite.teamName}
              </Text>
              <Text fontSize="xs" color="textMuted">
                Invited by {invite.inviterName}
                {user.teamId ? ' · Joining will leave your current team' : ''}
              </Text>
            </Box>
            <HStack gap={1}>
              <RiftboundButton
                size="xs"
                treatment="primary"
                onClick={() => openConsent(invite)}
                disabled={actionLoading}
              >
                Accept
              </RiftboundButton>
              <RiftboundButton
                size="xs"
                onClick={() => void handleDecline(invite)}
                disabled={actionLoading}
              >
                Decline
              </RiftboundButton>
            </HStack>
          </HStack>
        ))}
      </VStack>

      <JoinTeamConsentSheet
        open={Boolean(consentInvite)}
        teamName={consentInvite?.teamName ?? 'team'}
        shareCollection={shareCollection}
        onShareCollectionChange={setShareCollection}
        busy={busy}
        onClose={closeConsent}
        onAccept={() => void handleAccept()}
        onJoinWithoutSharing={() => void handleJoinWithoutSharing()}
      />
    </>
  );
}
